import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CronParser.module.css';

type ParsedCron = {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
};

const monthNames: Record<'zh-CN' | 'en-US', string[]> = {
  'zh-CN': ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
  'en-US': ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};

const weekNames: Record<'zh-CN' | 'en-US', string[]> = {
  'zh-CN': ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
  'en-US': ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};

const parseFieldDesc = (
  raw: string,
  type: 'minute' | 'hour' | 'dayOfMonth' | 'month' | 'dayOfWeek',
  lang: 'zh-CN' | 'en-US',
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  if (raw === '*') {
    if (type === 'minute') return t('cronParser.parseField.everyMinute');
    if (type === 'hour') return t('cronParser.parseField.everyHour');
    if (type === 'dayOfMonth') return t('cronParser.parseField.everyDayOfMonth');
    if (type === 'month') return t('cronParser.parseField.everyMonth');
    return t('cronParser.parseField.everyDayOfWeek');
  }

  if (raw.startsWith('*/')) {
    const step = Number(raw.slice(2));
    if (!Number.isInteger(step) || step <= 0) return t('cronParser.parseField.invalid', { raw });
    if (type === 'minute') return t('cronParser.parseField.everyNMinutes', { step });
    if (type === 'hour') return t('cronParser.parseField.everyNHours', { step });
    if (type === 'dayOfMonth') return t('cronParser.parseField.everyNDaysOfMonth', { step });
    if (type === 'month') return t('cronParser.parseField.everyNMonths', { step });
    return t('cronParser.parseField.everyNDaysOfWeek', { step });
  }

  if (raw.includes(',')) {
    const values = raw.split(',').map((v) => v.trim());
    const joined = lang === 'zh-CN' ? values.join('、') : values.join(', ');
    return t('cronParser.parseField.inList', { values: joined });
  }

  if (raw.includes('-')) {
    const [start, end] = raw.split('-');
    return t('cronParser.parseField.range', { start, end });
  }

  if (type === 'month') {
    const idx = Number(raw) - 1;
    if (idx >= 0 && idx < 12) return monthNames[lang][idx];
  }
  if (type === 'dayOfWeek') {
    const d = Number(raw) % 7;
    if (Number.isInteger(d) && d >= 0) return weekNames[lang][d];
  }

  return raw;
};

const inRange = (value: number, min: number, max: number) => value >= min && value <= max;

const matchesField = (field: string, value: number, min: number, max: number): boolean => {
  if (field === '*') return true;

  if (field.includes(',')) {
    return field.split(',').some((part) => matchesField(part.trim(), value, min, max));
  }

  if (field.startsWith('*/')) {
    const step = Number(field.slice(2));
    return Number.isInteger(step) && step > 0 && (value - min) % step === 0;
  }

  if (field.includes('-')) {
    const [startRaw, endRaw] = field.split('-');
    const start = Number(startRaw);
    const end = Number(endRaw);
    if (!Number.isInteger(start) || !Number.isInteger(end)) return false;
    return value >= start && value <= end;
  }

  const single = Number(field);
  if (!Number.isInteger(single)) return false;
  if (!inRange(single, min, max)) return false;
  return value === single;
};

const validateCron = (parts: string[], t: (key: string, options?: Record<string, unknown>) => string): string | null => {
  if (parts.length !== 5) return t('cronParser.validationError');
  return null;
};

const formatDateTime = (d: Date, locale: string) =>
  d.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

const calculateNextRuns = (parts: string[], count = 5, locale: string): string[] => {
  const [minField, hourField, domField, monthField, dowField] = parts;
  const results: string[] = [];
  const cursor = new Date();
  cursor.setSeconds(0, 0);
  cursor.setMinutes(cursor.getMinutes() + 1);

  let guard = 0;
  while (results.length < count && guard < 525600) {
    const minute = cursor.getMinutes();
    const hour = cursor.getHours();
    const dayOfMonth = cursor.getDate();
    const month = cursor.getMonth() + 1;
    const dayOfWeek = cursor.getDay();

    const matched =
      matchesField(minField, minute, 0, 59) &&
      matchesField(hourField, hour, 0, 23) &&
      matchesField(domField, dayOfMonth, 1, 31) &&
      matchesField(monthField, month, 1, 12) &&
      matchesField(dowField, dayOfWeek, 0, 7);

    if (matched) {
      results.push(formatDateTime(cursor, locale));
    }

    cursor.setMinutes(cursor.getMinutes() + 1);
    guard += 1;
  }

  return results;
};

const CronParser: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [expression, setExpression] = useState('*/5 * * * *');
  const [error, setError] = useState('');
  const lang: 'zh-CN' | 'en-US' = i18n.resolvedLanguage === 'zh-CN' ? 'zh-CN' : 'en-US';
  const locale = lang === 'zh-CN' ? 'zh-CN' : 'en-US';

  const parsed = useMemo<ParsedCron | null>(() => {
    const parts = expression.trim().split(/\s+/);
    const validationError = validateCron(parts, t);
    if (validationError) {
      setError(validationError);
      return null;
    }
    setError('');
    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
    return { minute, hour, dayOfMonth, month, dayOfWeek };
  }, [expression, t]);

  const nextRuns = useMemo(() => {
    if (!parsed) return [];
    try {
      return calculateNextRuns([parsed.minute, parsed.hour, parsed.dayOfMonth, parsed.month, parsed.dayOfWeek], 5, locale);
    } catch {
      return [];
    }
  }, [parsed, locale]);

  const minuteDesc = parsed ? parseFieldDesc(parsed.minute, 'minute', lang, t) : '';
  const hourDesc = parsed ? parseFieldDesc(parsed.hour, 'hour', lang, t) : '';
  const dayOfMonthDesc = parsed ? parseFieldDesc(parsed.dayOfMonth, 'dayOfMonth', lang, t) : '';
  const monthDesc = parsed ? parseFieldDesc(parsed.month, 'month', lang, t) : '';
  const dayOfWeekDesc = parsed ? parseFieldDesc(parsed.dayOfWeek, 'dayOfWeek', lang, t) : '';

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.title}>{t('cronParser.title')}</h2>
        <p className={styles.description}>{t('cronParser.description')}</p>

        <div className={styles.label}>{t('cronParser.labelExpression')}</div>
        <input
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
          className={styles.input}
          placeholder={t('cronParser.placeholderExpression')}
        />

        {error ? (
          <div className={`${styles.tip} ${styles.error}`}>{error}</div>
        ) : (
          parsed && (
            <>
              <div className={`${styles.tip} ${styles.success}`}>
                {t('cronParser.tipSuccess', {
                  minute: minuteDesc,
                  hour: hourDesc,
                  dayOfMonth: dayOfMonthDesc,
                  month: monthDesc,
                  dayOfWeek: dayOfWeekDesc
                })}
              </div>
              <div className={styles.nextRunsTitle}>{t('cronParser.nextRunsTitle')}</div>
              <ul className={styles.nextRunsList}>
                {nextRuns.length > 0 ? (
                  nextRuns.map((item) => <li key={item}>{item}</li>)
                ) : (
                  <li>{t('cronParser.nextRunsEmpty')}</li>
                )}
              </ul>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default CronParser;
